import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Activity, Share2, MessagesSquare, Brain, Sparkles, Maximize2 } from "lucide-react";
import { api } from "@/lib/api";
import KnowledgeGraph from "@/components/KnowledgeGraph";
import CognitiveArchitecture from "@/components/cognitive/CognitiveArchitecture";
import FeatureVectorSpace from "@/components/cognitive/FeatureVectorSpace";
import RecognitionPipeline from "@/components/cognitive/RecognitionPipeline";
import GrowthChart from "@/components/cognitive/GrowthChart";
import ConfessionLog from "@/components/cognitive/ConfessionLog";
import CrossPortfolioFeed from "@/components/cognitive/CrossPortfolioFeed";
import HiveBusFeed from "@/components/cognitive/HiveBusFeed";

// AVA's "Cognitive Telemetry" - the right-side companion to the Brain Console,
// showing four cinematic modules stacked and scrolled together:
//   1. Living Knowledge Graph (physics-simulated Hebbian synapse space)
//   2. Cognitive Architecture (hippocampus ↔ neocortex symbiosis)
//   3. Feature Vector Space (512-d CLIP embeddings + F(x+y)=Z structural invariance)
//   4. Recognition Pipeline (Mask R-CNN → YOLO11n → CLIP → Bayesian, >98% accuracy)
// plus a compact System Vitals footer and the Distilled Principles chip cloud.

export default function VitalsPanel({ refreshSignal }) {
  const [graph, setGraph] = useState({ nodes: [], links: [] });
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);

  const fetchData = useCallback(() => {
    api.get("/brain/graph").then((res) => setGraph(res.data)).catch(() => {});
    api.get("/brain/stats").then((res) => setStats(res.data)).catch(() => {});
    api.get("/brain/meta-concepts").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
    if (refreshSignal > 0) {
      const t = setTimeout(fetchData, 9000);
      return () => clearTimeout(t);
    }
  }, [fetchData, refreshSignal]);

  return (
    <div
      className="hidden md:col-span-3 md:flex h-full flex-col gap-3 overflow-y-auto pr-1 min-h-0"
      data-testid="vitals-panel"
    >
      {/* Living Knowledge Graph - headline module, gets the tallest fixed height
          so the physics simulation has real room to breathe on desktop screens. */}
      <div className="glass-panel rounded-2xl p-4 flex flex-col shrink-0" data-testid="living-knowledge-graph">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-cyan-400"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
              Living Knowledge Graph
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">
              Hebbian Synapse Space
            </span>
            <button
              data-testid="expand-neocortex-button"
              onClick={() => window.dispatchEvent(new CustomEvent("ava:navigate", { detail: "neocortex" }))}
              title="Open the full Neocortex view"
              className="p-1 rounded-md bg-white/5 border border-white/10 text-slate-400 hover:text-cyan-200 transition-colors"
            >
              <Maximize2 className="w-3 h-3" />
            </button>
          </div>
        </div>
        <div className="h-[280px] min-h-[220px] relative overflow-hidden rounded-lg">
          <KnowledgeGraph data={graph} />
        </div>
        <p className="text-[8px] leading-relaxed text-slate-600 font-mono-data mt-2">
          Cells that fire together, wire together - every exchange reinforces synapses; unused ones decay over 24h and are pruned below strength 0.05.
        </p>
      </div>

      <CognitiveArchitecture />
      <FeatureVectorSpace />
      <RecognitionPipeline />
      <GrowthChart />
      <HiveBusFeed />
      <CrossPortfolioFeed />
      <ConfessionLog />

      {meta && meta.hub_concepts && meta.hub_concepts.length > 0 && (
        <div className="glass-panel rounded-2xl p-4" data-testid="distilled-principles">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">Distilled Principles</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {meta.hub_concepts.map((h) => (
              <span
                key={h.concept}
                title={`${h.degree} cross-referenced links`}
                className="text-[9px] font-mono-data text-violet-300 bg-violet-500/10 border border-violet-400/25 rounded-full px-2 py-1"
              >
                {h.concept}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="glass-panel rounded-2xl p-4" data-testid="system-vitals">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">System Vitals</span>
        </div>
        {stats ? (
          <div className="grid grid-cols-2 gap-3 text-center">
            <VitalStat label="Nodes" value={stats.total_nodes} icon={<Share2 className="w-3 h-3" />} />
            <VitalStat label="Synapses" value={stats.total_edges} icon={<Share2 className="w-3 h-3" />} />
            <VitalStat label="Sessions" value={stats.total_conversations} icon={<MessagesSquare className="w-3 h-3" />} />
            <VitalStat label="Avg Strength" value={stats.avg_synaptic_strength} icon={<Activity className="w-3 h-3" />} />
          </div>
        ) : (
          <p className="text-[11px] text-slate-600">Loading vitals...</p>
        )}
      </div>
    </div>
  );
}

function VitalStat({ label, value }) {
  return (
    <div className="bg-[#0B0E14] border border-[#1A202C] rounded-lg py-3">
      <motion.p className="text-lg font-display font-bold text-white" key={value} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }}>
        {value}
      </motion.p>
      <p className="text-[9px] font-mono-data uppercase tracking-wider text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
